// Client-side wrappers for the subscription billing API.
//
// Every one of these hits a route that is gated server-side (checkout is
// owner/admin, portal and cancel are owner only), so a non-owner calling them
// gets a 403 with a message worth showing. Nothing here decides who may bill -
// it only asks, and reports what came back.
//
// No Stripe key of any kind lives on this side. The server creates the session
// and hands back a URL; the browser's only job is to follow it.

import axios from 'axios';

const API = () => process.env.REACT_APP_API_URL;

// Pull something readable out of an axios failure. The billing routes answer
// 400 with { error } for the configuration problems that actually happen -
// no price id for the plan, no provider selected, nothing to cancel - and
// those messages are written for a human to read.
export const billingErrorMessage = (err, fallback) =>
  err?.response?.data?.error || fallback;

// Checkout refused because the company already has a live subscription. The
// server's message says to use Manage Billing, so a caller that can open the
// portal should offer it.
export const isAlreadySubscribed = (err) =>
  err?.response?.data?.code === 'ALREADY_SUBSCRIBED';

/**
 * Start Stripe Checkout for a plan and send the browser to it.
 *
 * Does not return on success: the redirect leaves the page. Throws on failure
 * so the caller can surface the message and re-enable its button.
 */
export const startCheckout = async (planId) => {
  const response = await axios.post(`${API()}/api/billing/checkout`, { planId });
  const url = response.data?.url;

  if (!url) {
    throw new Error('Checkout is not available right now. Please try again.');
  }

  // Full navigation rather than react-router: the destination is Stripe.
  window.location.href = url;
};

/** Open the Stripe-hosted billing portal (card, invoices, cancellation). */
export const openBillingPortal = async () => {
  const response = await axios.post(`${API()}/api/billing/portal`);
  const url = response.data?.url;

  if (!url) {
    throw new Error('The billing portal is not available right now.');
  }

  window.location.href = url;
};

/** Current subscription state, read from our webhook-synced columns. */
export const fetchBillingStatus = async () => {
  const response = await axios.get(`${API()}/api/billing/status`);
  return response.data;
};

/**
 * Cancel at period end. returnMonth is 'YYYY-MM' or null for "Not sure".
 * Returns { cancelAt, returnMonth }.
 */
export const cancelSubscription = async (returnMonth = null) => {
  const response = await axios.post(`${API()}/api/billing/cancel`, { returnMonth: returnMonth || null });
  return response.data;
};

/**
 * Set or clear (null) the month the owner expects to be back. Owner only, and
 * only while a cancellation is pending or the subscription has ended (409
 * otherwise). Returns { returnMonth }.
 */
export const saveReturnMonth = async (returnMonth) => {
  const response = await axios.put(`${API()}/api/billing/return-month`, { returnMonth: returnMonth || null });
  return response.data;
};
